//
// instagram.com web JSON ("own" lane): profile by handle and the top-search variant for user search.
//   GET /api/v1/users/web_profile_info/?username=<u>
//   GET /web/search/topsearch/?context=blended&query=<q>&include_reel=true
// Headers: X-IG-App-ID 936619743392459 + a browser fingerprint. Instagram blocks many datacenter
// IPs (HTTP 400/401 or a login-wall page): those count as failures, the breaker decays the lane,
// and the estate-a lanes + RapidAPI carry (plan §8).
//

import { ProviderMiss, type Provider } from '../../router/registry';
import { browserHeaders, parseIgUserSearch, toIgProfile } from '../../router/shapes';

const ORIGIN = 'https://www.instagram.com';
const IG_APP_ID = '936619743392459';

function igHeaders(referer: string): Record<string, string> {
	return browserHeaders({
		'X-IG-App-ID': IG_APP_ID,
		'X-Requested-With': 'XMLHttpRequest',
		Referer: referer,
		'Sec-Fetch-Site': 'same-origin',
		'Sec-Fetch-Mode': 'cors',
		'Sec-Fetch-Dest': 'empty',
	});
}

export const IgDirect: Provider = {
	id: 'igdirect',
	platform: 'instagram',
	capabilities: ['search_users', 'profile'],
	family: 'own',
	transport: 'browser',
	requiresToken: false,
	seedLatencyMs: 800,
	// OFF by default: production egress is refused (HTTP 400 "SecFetch Policy violation" on search, 401 on profile), so as
	// the lowest-seeded lane it burned every first attempt. Still registered; SOCIAL_PROV_IGDIRECT_ENABLED=true turns it on.
	defaultEnabled: false,
	missStatuses: [404],
	request: (req) => {
		if (req.capability === 'profile') {
			return {
				url: `${ORIGIN}/api/v1/users/web_profile_info/?username=${encodeURIComponent(req.query)}`,
				init: { method: 'GET', headers: igHeaders(`${ORIGIN}/${encodeURIComponent(req.query)}/`) },
			};
		}
		return {
			url: `${ORIGIN}/web/search/topsearch/?context=blended&query=${encodeURIComponent(req.query)}&include_reel=true`,
			init: { method: 'GET', headers: igHeaders(`${ORIGIN}/`) },
		};
	},
	parser: (body, req) => {
		if (typeof body === 'string') throw new Error('non-JSON body (login wall?)');
		const b: any = body;
		if (b?.require_login || b?.status === 'fail') throw new Error(String(b?.message || 'blocked'));
		if (req.capability === 'profile') {
			// Only a well-formed "user: null" answer is an absence — and it still needs a second source to agree
			// (hedge.ts miss-consensus): hidden / region-restricted accounts look the same from here.
			if (b?.data && typeof b.data === 'object' && 'user' in b.data && b.data.user === null) throw new ProviderMiss('user is null');
			if (!b?.data?.user) throw new Error('malformed profile body');
			return toIgProfile(b.data.user);
		}
		return parseIgUserSearch(b, req.limit);
	},
};

export default IgDirect;
